import React, { FC, useEffect, useMemo, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { deposit, invest } from '@ally/data-selectors-afg-accounts'
import { BeneficiariesTabs } from './BeneficiariesTabs'
import { useHostServices } from '../HostServices'
import { InvestBeneficiaries } from './InvestBeneficiaries'
import { IraBeneficiaries } from './IraBeneficiaries'
import { NonIraBeneficiaries } from './NonIraBeneficiaries'
import { ACCOUNT_TYPE } from '../utils/constants'
import { BeneficiaryTab } from '../types'
import { NonIraBeneficiariesProvider } from '../services/NonIraBeneficiariesProvider'
import { IraBeneficiariesProvider } from '../services/IraBeneficiariesProvider'

export const Beneficiaries: FC = () => {
  const { hostData } = useHostServices()
  const location = useLocation()
  const history = useHistory()
  const headerRef = useRef<HTMLHeadingElement | null>(null)
  const queryParamId = new URLSearchParams(location.search).get('id')

  const nonRetirementAccounts = useMemo(() => {
    return deposit.nonRetirement
      .getAll(hostData)
      .filter(acc => acc.status.toLocaleLowerCase() === 'active')
  }, [hostData])

  const retirementAccounts = useMemo(() => {
    return deposit.retirement
      .getAll(hostData)
      .filter(acc => acc.status.toLocaleLowerCase() === 'active')
  }, [hostData])

  const investAccounts = useMemo(() => {
    return invest
      .getAll(hostData)
      .filter(acc => acc.status.toLocaleLowerCase() === 'open')
  }, [hostData])

  const hasNonRetirementAccount = useMemo(() => {
    return !!nonRetirementAccounts.length
  }, [nonRetirementAccounts])

  const hasRetirementAccount = useMemo(() => {
    return !!retirementAccounts.length
  }, [retirementAccounts])

  const hasInvestAccount = useMemo(() => {
    return !!investAccounts.length
  }, [investAccounts])

  const deepLinkTabToActivate = useMemo(() => {
    if (
      nonRetirementAccounts.find(item => {
        return item.id === queryParamId
      })
    ) {
      return ACCOUNT_TYPE.DEPOSIT
    }
    if (
      retirementAccounts.find(item => {
        return item.id === queryParamId
      })
    ) {
      return ACCOUNT_TYPE.IRA
    }
    if (
      investAccounts.find(item => {
        return item.id === queryParamId
      })
    ) {
      return ACCOUNT_TYPE.INVEST
    }
    return undefined
  }, [queryParamId, nonRetirementAccounts, retirementAccounts, investAccounts])

  const allTabs: BeneficiaryTab[] = [
    {
      tabName: ACCOUNT_TYPE.DEPOSIT,
      tabDetails: {
        label: 'Bank Checking, Savings and CDs',
        panel: (
          <NonIraBeneficiariesProvider>
            <NonIraBeneficiaries headerRef={headerRef} />
          </NonIraBeneficiariesProvider>
        ),
      },
      tabExists: hasNonRetirementAccount,
    },
    {
      tabName: ACCOUNT_TYPE.IRA,
      tabDetails: {
        label: 'Bank IRAs',
        panel: (
          <IraBeneficiariesProvider>
            <IraBeneficiaries headerRef={headerRef} />
          </IraBeneficiariesProvider>
        ),
      },
      tabExists: hasRetirementAccount,
    },
    {
      tabName: ACCOUNT_TYPE.INVEST,
      tabDetails: {
        label: 'Investments',
        panel: <InvestBeneficiaries headerRef={headerRef} />,
      },
      tabExists: hasInvestAccount,
    },
  ]

  const currentTabs = allTabs.filter(tab => tab.tabExists)
  const tabNames = currentTabs.map((tab: BeneficiaryTab) => tab.tabName)
  const currentPath = useMemo(() => {
    return location.pathname.substring(location.pathname.lastIndexOf('/') + 1)
  }, [location.pathname])

  const getActiveTab = (): number => {
    switch (currentPath) {
      case ACCOUNT_TYPE.DEPOSIT:
        return tabNames.indexOf(ACCOUNT_TYPE.DEPOSIT)
      case ACCOUNT_TYPE.IRA:
        return tabNames.indexOf(ACCOUNT_TYPE.IRA)
      case ACCOUNT_TYPE.INVEST:
        return tabNames.indexOf(ACCOUNT_TYPE.INVEST)
      default:
        return 0
    }
  }

  const defaultTitle = document.title

  useEffect(() => {
    switch (location.pathname) {
      case '/beneficiaries':
      case '/beneficiaries/deposit':
      case '/beneficiaries/ira':
      case '/beneficiaries/invest':
        document.title = 'Beneficiaries | Ally'
        break
      default:
        document.title = defaultTitle
    }
  }, [location.pathname])

  // This useEffect handles changing the route when the user manually enters a new path.
  // This also changes the URL if a user tries to hit a URL of an account they do not have
  // ex. '/beneficiaries/invest' will change to '/beneficiaries/deposit' if a user only has non-IRA accounts.
  useEffect(() => {
    if (tabNames.length) {
      if (deepLinkTabToActivate) {
        if (deepLinkTabToActivate === ACCOUNT_TYPE.IRA) {
          // istanbul ignore next - if the retirement account was found, the plan number must exist
          const planId = retirementAccounts.find(item => {
            return item.id === queryParamId
          })?.meta?.retirementPlanNumber
          history.push(`/beneficiaries/${deepLinkTabToActivate}`, {
            deepLinkId: planId,
          })
        } else {
          history.push(`/beneficiaries/${deepLinkTabToActivate}`, {
            deepLinkId: queryParamId,
          })
        }
      } else {
        const selectedItemIndex = tabNames.indexOf(currentPath)
        if (selectedItemIndex === -1) {
          history.push(`/beneficiaries/${tabNames[0]}`)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTabs, deepLinkTabToActivate])

  // Only render tabs when user has more than one account type (Non-IRA, IRA, Investments)
  if (currentTabs.length > 1) {
    return (
      <BeneficiariesTabs
        tabs={currentTabs}
        activeTab={getActiveTab()}
        headerRef={headerRef}
      />
    )
  }
  if (hasNonRetirementAccount) {
    return (
      <NonIraBeneficiariesProvider>
        <NonIraBeneficiaries headerRef={headerRef} />
      </NonIraBeneficiariesProvider>
    )
  }
  if (hasRetirementAccount) {
    return (
      <IraBeneficiariesProvider>
        <IraBeneficiaries headerRef={headerRef} />
      </IraBeneficiariesProvider>
    )
  }
  if (hasInvestAccount) {
    return <InvestBeneficiaries headerRef={headerRef} />
  }
  return <></>
}
